
/* ═══════════════════════════════════════════════════════════
   UI PRIMITIVES — CSS Module
   Design system matching signup-ai chat widget
   ═══════════════════════════════════════════════════════════ */

/* ─── ACCESSIBILITY HELPERS ─── */
/* Visually hidden but exposed to assistive technology. Used by Table's
   <caption> so screen-reader users get an accessible table name without
   altering the visual design. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ─── BUTTON ─── */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 600;
  border: none;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s ease;
  min-width: 44px;
  gap: 8px;
  outline: none;
  position: relative;
  overflow: hidden;
}

.btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 210, 106, 0.2);
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

/* Sizes */
.btnSm { height: 36px; padding: 0 14px; font-size: 13px; gap: 6px; border-radius: 10px; }
.btnMd { height: 42px; padding: 0 18px; font-size: 14px; }
.btnLg { height: 48px; padding: 0 24px; font-size: 15px; }

/* Variants */
.btnPrimary {
  background: linear-gradient(135deg, #00D26A 0%, #14B8A6 100%);
  color: #0A0E1A;
  box-shadow: 0 2px 16px rgba(0, 210, 106, 0.2);
}
.btnPrimary:hover {
  box-shadow: 0 4px 24px rgba(0, 210, 106, 0.3);
  transform: translateY(-1px);
}
.btnPrimary:active { transform: translateY(0); }

.btnSecondary {
  background: #1E2540;
  color: #CBD5E1;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.btnSecondary:hover {
  background: #252D4A;
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.btnGhost {
  background: transparent;
  color: #8B92A8;
}
.btnGhost:hover {
  background: rgba(255, 255, 255, 0.04);
  color: #CBD5E1;
}

.btnDanger {
  background: rgba(239, 68, 68, 0.12);
  color: #EF4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}
.btnDanger:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.35);
}

.btnFinancial {
  background: linear-gradient(135deg, #00D26A 0%, #14B8A6 100%);
  color: #0A0E1A;
  box-shadow: 0 2px 16px rgba(0, 210, 106, 0.2);
}
.btnFinancial:hover {
  box-shadow: 0 4px 24px rgba(0, 210, 106, 0.3);
  transform: translateY(-1px);
}

.btnGradient {
  background: linear-gradient(135deg, #00D26A 0%, #14B8A6 40%, #7B5CFA 100%);
  color: #fff;
  box-shadow: 0 2px 20px rgba(0, 210, 106, 0.2);
}
.btnGradient:hover {
  box-shadow: 0 4px 28px rgba(0, 210, 106, 0.3);
  transform: translateY(-1px);
}

.btnFullWidth { width: 100%; }

.spinner {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ─── CARD ─── */
.card {
  background: #0F1424;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  padding: 24px;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
}

.cardHoverable:hover {
  border-color: rgba(0, 210, 106, 0.12);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 210, 106, 0.04);
}

.cardHeader {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
}

.cardTitle {
  color: #fff;
  font-weight: 600;
  font-size: 17px;
  line-height: 1.3;
}

.cardDescription {
  color: #8B92A8;
  font-size: 13.5px;
  line-height: 1.5;
}

.cardFooter {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

/* ─── INPUT ─── */
.inputWrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.inputLabel {
  font-size: 13px;
  font-weight: 500;
  color: #8B92A8;
}

.inputField {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  background: #131830;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: all 0.2s ease;
}

.inputField::placeholder { color: #555C72; }

.inputField:focus {
  border-color: #00D26A;
  box-shadow: 0 0 0 3px rgba(0, 210, 106, 0.08);
  background: #161C34;
}

.inputFieldError {
  border-color: #EF4444;
}
.inputFieldError:focus {
  border-color: #EF4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.inputFieldDisabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #1A2040;
}

.inputFieldWithLeftIcon { padding-left: 40px; }
.inputFieldWithRightIcon { padding-right: 40px; }

.inputIcon {
  position: absolute;
  display: flex;
  align-items: center;
  color: #555C72;
  pointer-events: none;
}
.inputIconLeft { left: 14px; }
.inputIconRight { right: 14px; }

.inputError {
  font-size: 12px;
  color: #EF4444;
}

.inputHelper {
  font-size: 12px;
  color: #555C72;
}

/* ─── SELECT ─── */
.selectField {
  width: 100%;
  height: 44px;
  padding: 0 40px 0 14px;
  background: #131830;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  appearance: none;
  transition: all 0.2s ease;
}

.selectField:focus {
  border-color: #00D26A;
  box-shadow: 0 0 0 3px rgba(0, 210, 106, 0.08);
  background: #161C34;
}

.selectChevron {
  position: absolute;
  right: 14px;
  display: flex;
  align-items: center;
  color: #555C72;
  pointer-events: none;
}

/* ─── TEXTAREA ─── */
.textareaField {
  width: 100%;
  padding: 12px 14px;
  background: #131830;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: all 0.2s ease;
}

.textareaField::placeholder { color: #555C72; }

.textareaField:focus {
  border-color: #00D26A;
  box-shadow: 0 0 0 3px rgba(0, 210, 106, 0.08);
  background: #161C34;
}

/* ─── MODAL ─── */
.modalBackdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(5, 7, 15, 0.75);
  backdrop-filter: blur(4px);
}

.modalContent {
  position: relative;
  width: 100%;
  background: #0F1424;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(0, 210, 106, 0.04);
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
}

.modalSm { max-width: 384px; }
.modalMd { max-width: 448px; }
.modalLg { max-width: 512px; }
.modalXl { max-width: 576px; }
.modalFull { max-width: 95vw; max-height: 95vh; }

.modalHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.modalTitle {
  color: #fff;
  font-weight: 600;
  font-size: 17px;
}

.modalClose {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: #555C72;
  cursor: pointer;
  transition: all 0.15s ease;
}

.modalClose:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #CBD5E1;
}

.modalBody {
  padding: 24px;
}

/* ─── BADGE ─── */
.badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

/* ─── TABLE ─── */
.tableWrapper {
  width: 100%;
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  overflow: hidden;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.tableHead {
  background: #131830;
}

.tableHeadCell {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #555C72;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.tableRow {
  background: #0F1424;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  transition: background 0.15s ease;
}

.tableRow:hover {
  background: #141A30;
}

.tableCell {
  padding: 14px 16px;
  color: #CBD5E1;
}

/* ─── TABS ─── */
.tabList {
  display: flex;
  align-items: center;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  overflow-x: auto;
}

.tab {
  position: relative;
  padding: 12px 18px;
  font-size: 13.5px;
  font-weight: 500;
  font-family: inherit;
  color: #555C72;
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.tab:hover {
  color: #CBD5E1;
}

.tabActive {
  color: #fff;
}

.tabIndicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #00D26A, #14B8A6);
  border-radius: 2px 2px 0 0;
}

.tabDisabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* ─── TOAST ─── */
.toastContainer {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #0F1424;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-left: 3px solid;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  min-width: 300px;
  max-width: 420px;
  pointer-events: auto;
}

.toastSuccess { border-left-color: #10B981; }
.toastError { border-left-color: #EF4444; }
.toastWarning { border-left-color: #FB923C; }
.toastInfo { border-left-color: #3B82F6; }

.toastTitle {
  font-size: 13.5px;
  font-weight: 600;
  color: #fff;
}

.toastDescription {
  font-size: 12px;
  color: #8B92A8;
  margin-top: 2px;
}

.toastClose {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #555C72;
  cursor: pointer;
  transition: all 0.15s ease;
}
.toastClose:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #CBD5E1;
}

/* ─── PROGRESS BAR ─── */
.progressTrack {
  width: 100%;
  border-radius: 9999px;
  overflow: hidden;
  background: #1A2040;
}
.progressTrackSm { height: 4px; }
.progressTrackMd { height: 8px; }
.progressTrackLg { height: 14px; }

.progressFill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.5s ease-out;
}

.progressGreen { background: linear-gradient(90deg, #00D26A, #14B8A6); }
.progressViolet { background: linear-gradient(90deg, #7B5CFA, #A78BFA); }
.progressWarning { background: #FB923C; }
.progressDanger { background: #EF4444; }
.progressInfo { background: #3B82F6; }
.progressSuccess { background: #10B981; }

.progressLabel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.progressLabelText {
  font-size: 13px;
  font-weight: 500;
  color: #8B92A8;
}

.progressLabelValue {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

/* ─── SKELETON ─── */
.skeleton {
  background: linear-gradient(90deg, #1A2040 25%, #252D4A 50%, #1A2040 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}
.skeletonText { height: 16px; border-radius: 8px; }
.skeletonCircular { border-radius: 9999px; }
.skeletonRect { border-radius: 12px; }

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ─── EMPTY STATE ─── */
.emptyState {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px 24px;
}

.emptyStateIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #131830;
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #555C72;
  margin-bottom: 16px;
}

.emptyStateTitle {
  color: #fff;
  font-weight: 600;
  font-size: 17px;
  margin-bottom: 8px;
}

.emptyStateDesc {
  color: #8B92A8;
  font-size: 13.5px;
  max-width: 360px;
  margin-bottom: 24px;
  line-height: 1.5;
}

/* ─── DROPDOWN ─── */
.dropdown {
  position: relative;
  display: inline-block;
}

.dropdownTrigger {
  cursor: pointer;
}

.dropdownMenu {
  position: absolute;
  z-index: 50;
  margin-top: 6px;
  min-width: 180px;
  padding: 6px;
  background: #131830;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 210, 106, 0.03);
}

.dropdownMenuLeft { left: 0; }
.dropdownMenuRight { right: 0; }

.dropdownItem {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  font-size: 13.5px;
  font-family: inherit;
  color: #CBD5E1;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}

.dropdownItem:hover {
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
}

.dropdownItemDanger {
  color: #EF4444;
}
.dropdownItemDanger:hover {
  background: rgba(239, 68, 68, 0.08);
}

.dropdownItemDisabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.dropdownItemIcon {
  flex-shrink: 0;
  color: #555C72;
}

.dropdownDivider {
  margin: 4px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

/* ─── AVATAR ─── */
.avatar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  flex-shrink: 0;
  overflow: hidden;
  font-weight: 600;
  color: #fff;
  user-select: none;
}

.avatarSm { width: 32px; height: 32px; font-size: 11px; }
.avatarMd { width: 40px; height: 40px; font-size: 13px; }
.avatarLg { width: 48px; height: 48px; font-size: 15px; }
.avatarXl { width: 64px; height: 64px; font-size: 18px; }

.avatarImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

